import { Alert, Button, Checkbox, EmptyState, LoadingState, useToast } from '@orbind/ui'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PERMISSAO_DO_CONECTOR_ROTULO, type PermissaoDoConector } from '@orbind/core'
import { useSession } from '../../lib/session.tsx'
import {
  quandoFoi,
  useEventosDoWorkspace,
  useLiberarPessoa,
  usePessoasDoConector,
  type PessoaDoConector,
} from '../ai-connector/useConectorDeIa.ts'
import './conectores-de-ia.css'

/**
 * Conectores de IA (SPEC-20261103-1818) — a tela em que o administrador diz
 * QUEM pode usar o Orbind pelo Claude e COM QUE permissões.
 *
 * Uma linha por pessoa do workspace, sem modal: marcar a pessoa libera com
 * as permissões padrão; as caixinhas de permissão aparecem na própria linha
 * e gravam ao clicar. "Consultar" não se desmarca — sem ela o conector não
 * serve para nada; para tirar tudo, desliga-se a pessoa.
 */

const ROTULO_DA_FERRAMENTA: Record<string, string> = {
  meus_itens: 'Consultou os próprios Itens',
  buscar_itens: 'Buscou Itens',
  ver_item: 'Abriu um Item',
  catalogos: 'Leu os catálogos',
  atualizar_item: 'Alterou um Item',
  comentar_item: 'Comentou',
  concluir_item: 'Mudou o status',
  criar_item: 'Criou um Item',
}

export function ConectoresDeIaSection() {
  const navigate = useNavigate()
  const toast = useToast()
  const sessao = useSession()
  const consulta = usePessoasDoConector()
  const eventos = useEventosDoWorkspace()
  const liberar = useLiberarPessoa()
  const [filtro, setFiltro] = useState('')

  if (consulta.isPending) return <LoadingState variant="detail" label="Carregando as pessoas" />
  if (consulta.isError) {
    return (
      <Alert
        tone="danger"
        title="Não foi possível carregar os conectores de IA"
        action={
          <Button size="sm" icon="fas fa-redo" onClick={() => void consulta.refetch()}>
            Tentar de novo
          </Button>
        }
      >
        {consulta.error instanceof Error ? consulta.error.message : 'Tente novamente em instantes.'}
      </Alert>
    )
  }

  const { pessoas, permissoes } = consulta.data
  const termo = filtro.trim().toLowerCase()
  const visiveis = termo
    ? pessoas.filter((p) => p.name.toLowerCase().includes(termo) || p.email.toLowerCase().includes(termo))
    : pessoas
  const liberadas = pessoas.filter((p) => p.liberado).length

  const salvar = (pessoa: PessoaDoConector, liberado: boolean, novas?: PermissaoDoConector[]) =>
    liberar.mutate(
      { memberId: pessoa.memberId, liberado, permissoes: novas },
      {
        onSuccess: () =>
          toast.push({
            tone: 'success',
            message: liberado
              ? pessoa.memberId === sessao.id
                ? 'Você pode usar o Orbind pelo Claude. Agora abra Conectar ao Claude e gere o token.'
                : `${pessoa.name} pode usar o Orbind pelo Claude.`
              : `${pessoa.name} não usa mais o Orbind pelo Claude; os tokens dela foram revogados.`,
          }),
        onError: (e) =>
          toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível salvar.' }),
      },
    )

  const alternarPermissao = (pessoa: PessoaDoConector, p: PermissaoDoConector) => {
    if (p === 'consultar') return
    const tem = pessoa.permissoes.includes(p)
    const novas = tem ? pessoa.permissoes.filter((x) => x !== p) : [...pessoa.permissoes, p]
    salvar(pessoa, true, novas)
  }

  return (
    <div className="cia">
      <header className="cia__head">
        <h2 className="cia__title">Conectores de IA</h2>
        <p className="cia__sub">
          Quem está liberado aqui gera um token pessoal na tela <strong>Conectar ao Claude</strong> (menu do usuário)
          e passa a consultar, alterar, comentar, concluir e criar Itens de Trabalho de dentro do Claude. O conector
          age como a pessoa: vê o que ela vê e respeita o perfil dela; as permissões abaixo são um teto por cima
          disso.
        </p>
        <div className="cia__acoes">
          <Button variant="secondary" icon="fas fa-robot" onClick={() => navigate('/claude')}>
            Minha tela Conectar ao Claude
          </Button>
        </div>
      </header>

      <section className="cia__bloco" aria-label="Pessoas">
        <div className="cia__bloco-head">
          <h3 className="cia__h3">
            Pessoas · {liberadas} de {pessoas.length} liberada{liberadas === 1 ? '' : 's'}
          </h3>
          <input
            className="ob-input cia__busca"
            placeholder="Buscar pessoa"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            aria-label="Buscar pessoa"
          />
        </div>
        {visiveis.length === 0 ? (
          <EmptyState
            icon="fas fa-user-friends"
            title={termo ? 'Ninguém com esse nome' : 'Nenhuma pessoa no workspace'}
            description={termo ? 'Tente outro nome ou e-mail.' : 'Convide pessoas em Membros para liberar o conector.'}
          />
        ) : (
          <ul className="cia__lista">
            {visiveis.map((pessoa) => (
              <li key={pessoa.memberId} className={`cia__pessoa${pessoa.liberado ? ' cia__pessoa--liberada' : ''}`}>
                <div className="cia__pessoa-main">
                  <Checkbox
                    label={
                      <span className="cia__nome">
                        {pessoa.name}
                        {!pessoa.ativo ? <span className="cia__inativa"> · inativa</span> : null}
                      </span>
                    }
                    checked={pessoa.liberado}
                    disabled={liberar.isPending}
                    onChange={(e) => salvar(pessoa, e.target.checked)}
                  />
                  <span className="cia__email">{pessoa.email}</span>
                  {/* situação nunca só por cor: ícone + palavra */}
                  <span className="cia__situacao">
                    <i className={pessoa.liberado ? 'fas fa-check-circle' : 'far fa-circle'} aria-hidden />
                    {pessoa.liberado
                      ? `Liberada · ${pessoa.tokensAtivos} token${pessoa.tokensAtivos === 1 ? '' : 's'} · último uso ${quandoFoi(pessoa.ultimoUso)}`
                      : 'Sem acesso pelo Claude'}
                  </span>
                </div>
                {pessoa.liberado ? (
                  <div className="cia__permissoes" aria-label={`Permissões de ${pessoa.name}`}>
                    {permissoes.map((p) => (
                      <Checkbox
                        key={p}
                        label={p === 'consultar' ? 'consultar' : p}
                        title={PERMISSAO_DO_CONECTOR_ROTULO[p]}
                        checked={pessoa.permissoes.includes(p)}
                        disabled={p === 'consultar' || liberar.isPending}
                        onChange={() => alternarPermissao(pessoa, p)}
                      />
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="cia__bloco" aria-label="O que a IA fez">
        <h3 className="cia__h3">Últimas ações feitas pelo Claude</h3>
        {eventos.isPending ? (
          <LoadingState variant="list" label="Carregando as ações" />
        ) : eventos.isError ? (
          <p className="cia__vazio">Não foi possível carregar as ações.</p>
        ) : eventos.data.eventos.length === 0 ? (
          <p className="cia__vazio">Nenhuma ação ainda. Só escrita entra aqui: consultas não são registradas.</p>
        ) : (
          <ul className="cia__eventos">
            {eventos.data.eventos.map((ev) => (
              <li key={ev.id} className="cia__evento">
                <span className="cia__evento-quando">{quandoFoi(ev.createdAt)}</span>
                <span className="cia__evento-quem">{ev.memberName}</span>
                <span className="cia__evento-oque">
                  <span className="cia__evento-tipo">{ROTULO_DA_FERRAMENTA[ev.tool] ?? ev.tool}</span>
                  {' — '}
                  {ev.workItemId ? (
                    <button type="button" className="cia__link" onClick={() => navigate(`/work-items/${ev.workItemId}`)}>
                      {ev.summary}
                    </button>
                  ) : (
                    ev.summary
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
